import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { QuotationBlock, TourInfo, TransportationInfo, AccommodationInfo, FlightInfo, RestaurantInfo, FlightEvent, AccommodationEvent } from '@/components/quotation/types';
import { loadGoogleMapsApi } from '@/lib/google-maps-loader';

interface PageMapMobileProps {
  blocks: QuotationBlock[];
  tours: TourInfo[];
  transportations?: TransportationInfo[];
  restaurants?: RestaurantInfo[];
  accommodations?: AccommodationInfo[];
  flights?: FlightInfo[];
  flightEvents?: FlightEvent[];
  accommodationEvents?: AccommodationEvent[];
  showAccommodations?: boolean;
  visibleItemTypes?: string[];
}

type MapPoint = {
  id: string;
  title: string;
  coordinates?: {
    lat: number | string;
    lng: number | string;
  };
  type: 'block' | 'tour' | 'restaurant' | 'transportation' | 'transportation-departure' | 'transportation-arrival' | 
        'accommodation' | 'accommodation-checkin' | 'accommodation-checkout' |
        'flight' | 'flight-departure' | 'flight-arrival';
  globalOrder?: number;
};

export default function PageMapMobile({ 
  blocks, 
  tours, 
  transportations = [], 
  restaurants = [],
  accommodations = [],
  flights = [],
  flightEvents = [],
  accommodationEvents = [],
  showAccommodations = false,
  // Show same item types as desktop map by default
  visibleItemTypes = ['block', 'tour', 'restaurant', 'transportation', 'accommodation', 'accommodation-checkin', 'accommodation-checkout', 'flight-departure', 'flight-arrival']
}: PageMapMobileProps) {
  // Debug version tag com timestamp atual para quebrar o cache
  const versionTag = useMemo(() => `v${Math.floor(Date.now() / 60000)}`, []);
  const mapRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const activeInfoWindow = useRef<google.maps.InfoWindow | null>(null);
  const pointsRef = useRef<MapPoint[]>([]);
  
  // For debugging - log total number of points in each render
  const [pointCount, setPointCount] = useState(0);
  
  // State to track if maps API is loaded
  const [mapsLoaded, setMapsLoaded] = useState(false);
  
  // Helper function to check if a point should be displayed based on its type
  const shouldDisplayPoint = useCallback((type: string): boolean => {
    // Handle specific case for flight vs flight events
    if (type === 'flight') {
      // Regular flights shouldn't be displayed
      return false;
    }
    
    if (type === 'flight-departure' || type === 'flight-arrival') {
      // Show flight events only if they're explicitly allowed or if 'flight' is allowed
      return visibleItemTypes.includes('flight-departure') || 
             visibleItemTypes.includes('flight-arrival') || 
             visibleItemTypes.includes('flight');
    }
    
    // For all other types, check the base type
    const baseType = type.split('-')[0];
    return visibleItemTypes.includes(baseType) || visibleItemTypes.includes(type);
  }, [visibleItemTypes]);

  // Load Google Maps API
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapsLoaded(true);
    } else {
      loadGoogleMapsApi()
        .then(() => setMapsLoaded(true))
        .catch((error) => console.error('Error loading Google Maps API:', error));
    }
  }, []);
  
  // Process points data
  useEffect(() => {
    const points: MapPoint[] = [];
    
    // Add block points if they should be displayed
    if (shouldDisplayPoint('block')) {
    blocks.forEach(block => {
      // Skip page description blocks to match desktop behavior
      if ((block as any).isPageDescription) {
        return;
      }
      if (block.coordinates && 
          typeof block.coordinates.lat === 'number' && 
          typeof block.coordinates.lng === 'number' && 
          !(block.coordinates.lat === 0 && block.coordinates.lng === 0)) {
        points.push({
          id: block.id,
          title: block.title,
          coordinates: block.coordinates,
          type: 'block',
          globalOrder: block.globalOrder ?? 999999
        });
      }
    });
    }
    
    // Add tour points if they should be displayed
    if (shouldDisplayPoint('tour')) {
    tours.forEach(tour => {
      if (tour.coordinates && 
          typeof tour.coordinates.lat === 'number' && 
          typeof tour.coordinates.lng === 'number' && 
          !(tour.coordinates.lat === 0 && tour.coordinates.lng === 0)) {
        points.push({
          id: tour.id,
          title: tour.name,
          coordinates: tour.coordinates,
          type: 'tour',
          globalOrder: tour.globalOrder ?? 999999
        });
      }
    });
    }
    
    // Add restaurant points if they should be displayed
    if (shouldDisplayPoint('restaurant')) {
      restaurants.forEach(restaurant => {
        if (restaurant.coordinates && 
            typeof restaurant.coordinates.lat === 'number' && 
            typeof restaurant.coordinates.lng === 'number' && 
            !(restaurant.coordinates.lat === 0 && restaurant.coordinates.lng === 0)) {
          points.push({
            id: restaurant.id,
            title: restaurant.name,
            coordinates: restaurant.coordinates,
            type: 'restaurant',
            globalOrder: restaurant.globalOrder ?? 999999
          });
        }
      });
    }
    
    // Add transportation points if they should be displayed
    if (shouldDisplayPoint('transportation')) {
      transportations.forEach(transportation => {
        let addedAnyPoint = false;
        // Departure point
        if (transportation.departureCoordinates && 
            typeof transportation.departureCoordinates.lat === 'number' && 
            typeof transportation.departureCoordinates.lng === 'number' && 
            !(transportation.departureCoordinates.lat === 0 && transportation.departureCoordinates.lng === 0)) {
          points.push({
            id: `${transportation.id}-departure`,
            title: transportation.departureLocation || 'Partida',
            coordinates: transportation.departureCoordinates,
            type: 'transportation-departure',
            globalOrder: transportation.globalOrder ?? 999999
          });
          addedAnyPoint = true;
        }
        
        // Arrival point
        if (transportation.arrivalCoordinates && 
            typeof transportation.arrivalCoordinates.lat === 'number' && 
            typeof transportation.arrivalCoordinates.lng === 'number' && 
            !(transportation.arrivalCoordinates.lat === 0 && transportation.arrivalCoordinates.lng === 0)) {
          points.push({
            id: `${transportation.id}-arrival`,
            title: transportation.arrivalLocation || 'Chegada',
            coordinates: transportation.arrivalCoordinates,
            type: 'transportation-arrival',
            globalOrder: (transportation.globalOrder ?? 999999) + 0.5
          });
          addedAnyPoint = true;
        }
        
        // Fallback to generic coordinates when specific departure/arrival are missing
        if (!addedAnyPoint && transportation.coordinates &&
            typeof transportation.coordinates.lat === 'number' &&
            typeof transportation.coordinates.lng === 'number' &&
            !(transportation.coordinates.lat === 0 && transportation.coordinates.lng === 0)) {
          points.push({
            id: transportation.id,
            title: transportation.name || transportation.location || 'Transporte',
            coordinates: transportation.coordinates,
            type: 'transportation',
            globalOrder: transportation.globalOrder ?? 999999
          });
        }
      });
    }
    
    // Add accommodation points if they should be displayed
    if (showAccommodations && shouldDisplayPoint('accommodation')) {
      accommodations.forEach(accommodation => {
        if (accommodation.coordinates && 
            typeof accommodation.coordinates.lat === 'number' && 
            typeof accommodation.coordinates.lng === 'number' && 
            !(accommodation.coordinates.lat === 0 && accommodation.coordinates.lng === 0)) {
          points.push({
            id: accommodation.id,
            title: accommodation.name,
            coordinates: accommodation.coordinates,
            type: 'accommodation',
            globalOrder: accommodation.globalOrder ?? 999999
          });
        }
      });
    }
    
    // Process accommodation events (independent of showAccommodations)
    if (accommodationEvents && accommodationEvents.length > 0) {
      accommodationEvents.forEach(accEvent => {
        // Find the associated accommodation
        const accommodation = accommodations.find(acc => acc.id === accEvent.accommodationId);
        
        if (accommodation && accommodation.coordinates && 
            typeof accommodation.coordinates.lat === 'number' && 
            typeof accommodation.coordinates.lng === 'number' && 
            !(accommodation.coordinates.lat === 0 && accommodation.coordinates.lng === 0)) {
          
          points.push({
            id: accEvent.id,
            title: accEvent.title || (accEvent.isCheckIn ? 'Check-in' : 'Check-out'),
            coordinates: accommodation.coordinates,
            type: accEvent.isCheckIn ? 'accommodation-checkin' : 'accommodation-checkout',
            globalOrder: accEvent.globalOrder ?? accommodation.globalOrder ?? 999999
          });
          
          console.log(`[PageMapMobile] Adicionado ponto de ${accEvent.isCheckIn ? 'check-in' : 'check-out'} para acomodação ${accommodation.name}`, {
            eventId: accEvent.id,
            accommodationId: accEvent.accommodationId,
            coordinates: accommodation.coordinates
          });
        } else {
          console.warn(`[PageMapMobile] Evento de acomodação sem coordenadas válidas:`, {
            eventId: accEvent.id,
            accommodationId: accEvent.accommodationId,
            hasAccommodation: !!accommodation,
            coordinates: accommodation?.coordinates
          });
        }
      });
    }

    // Add flights as separate departure/arrival items when showAsSeparateEvents is enabled
    if (flights && flights.length > 0 && (shouldDisplayPoint('flight-departure') || shouldDisplayPoint('flight-arrival'))) {
      flights.forEach((flight, fIdx) => {
        if (!flight.showAsSeparateEvents) return;
        
        // Departure
        let depCoords = flight.departureCoordinates;
        const depValid = depCoords && typeof depCoords.lat === 'number' && typeof depCoords.lng === 'number' && !(depCoords.lat === 0 && depCoords.lng === 0);
        if (!depValid) {
          depCoords = { lat: -23.5505 + (fIdx * 0.01), lng: -46.6333 + (fIdx * 0.01) };
        }
        if (shouldDisplayPoint('flight-departure')) {
          points.push({
            id: `${flight.id}-departure`,
            title: `Partida: ${flight.departureAirport || ''}`,
            coordinates: depCoords,
            type: 'flight-departure',
            globalOrder: flight.departureGlobalOrder ?? flight.globalOrder ?? 999999
          });
        }
        
        // Arrival
        let arrCoords = flight.arrivalCoordinates;
        const arrValid = arrCoords && typeof arrCoords.lat === 'number' && typeof arrCoords.lng === 'number' && !(arrCoords.lat === 0 && arrCoords.lng === 0);
        if (!arrValid) {
          arrCoords = { lat: -23.5505 + (fIdx * 0.01), lng: -46.6333 + (fIdx * 0.01) };
        }
        if (shouldDisplayPoint('flight-arrival')) {
          const lastArrivalAirport = (flight.connections && flight.connections.length > 0)
            ? flight.connections[flight.connections.length - 1].arrivalAirport
            : flight.arrivalAirport;
          points.push({
            id: `${flight.id}-arrival`,
            title: `Chegada: ${lastArrivalAirport || ''}`,
            coordinates: arrCoords,
            type: 'flight-arrival',
            globalOrder: flight.arrivalGlobalOrder ?? flight.globalOrder ?? 999999
          });
        }
      });
    }
    
    // Process flight events only (not regular flights)
    // Since we've already checked in shouldDisplayPoint, we know regular flights won't be included
    const processedFlightIds = new Set<string>();
    
    if (flightEvents && flightEvents.length > 0) {
      flightEvents.forEach(flightEvent => {
        if (!shouldDisplayPoint(flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival')) {
          return;
        }
        
        const flight = flights.find(f => f.id === flightEvent.flightId);
        
        if (flight) {
          processedFlightIds.add(flight.id);
          
          const coordinates = flightEvent.isDeparture 
            ? flight.departureCoordinates 
            : flight.arrivalCoordinates;
          
          const title = flightEvent.isDeparture
            ? `Partida: ${flight.departureAirport}`
            : `Chegada: ${flight.arrivalAirport}`;
          
          if (coordinates && 
              typeof coordinates.lat === 'number' && 
              typeof coordinates.lng === 'number' && 
              !(coordinates.lat === 0 && coordinates.lng === 0)) {
            points.push({
              id: flightEvent.id,
              title: title,
              coordinates: coordinates,
              type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
              globalOrder: flightEvent.globalOrder ?? 999999
            });
          }
        }
      });
    }
    
    // We don't process regular flights at all since they shouldn't be displayed
    // This was explicitly requested by the user
    
    // Sort points and store in ref
    points.sort((a, b) => (a.globalOrder ?? 999999) - (b.globalOrder ?? 999999));
    pointsRef.current = points;
    setPointCount(points.length); // Update count for debugging
    
    // Clear existing markers if the component is re-rendering with new points
    if (mapInstanceRef.current && markersRef.current.length > 0) {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    }
    
  }, [blocks, tours, transportations, restaurants, accommodations, flights, flightEvents, showAccommodations, shouldDisplayPoint, accommodationEvents]);

  // Initialize and update map whenever mapsLoaded or pointCount changes
  useEffect(() => {
    // Only proceed if maps API is loaded, container exists, and we have points
    if (!mapsLoaded || !mapRef.current || pointsRef.current.length === 0) {
      return;
    }
    
    // Create map if it doesn't exist yet
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }]
        }
      ]
    });
    }
    
    const map = mapInstanceRef.current;
    const bounds = new window.google.maps.LatLngBounds();
    
    // Group transportation points by ID
    const transportationPointsByID = pointsRef.current
      .filter(point => point.type.startsWith('transportation'))
      .reduce((groups, point) => {
        const transportationId = point.id.split('-')[0];
        if (!groups[transportationId]) {
          groups[transportationId] = [];
        }
        groups[transportationId].push(point);
        return groups;
      }, {} as Record<string, MapPoint[]>);
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    // Create sequential counter for markers
    let visibleMarkerCounter = 1;
    
    // Create markers for each point
    pointsRef.current.forEach((point, index) => {
      if (!point.coordinates) return;
      
      // Convert coordinates to numbers
      const lat = typeof point.coordinates.lat === 'string' 
        ? parseFloat(point.coordinates.lat) 
        : Number(point.coordinates.lat);
      
      const lng = typeof point.coordinates.lng === 'string' 
        ? parseFloat(point.coordinates.lng) 
        : Number(point.coordinates.lng);
      
      if (isNaN(lat) || isNaN(lng)) return;
      
      const position = new window.google.maps.LatLng(lat, lng);
      bounds.extend(position);
      
      // Determine if this point should have a label
      const isTransportationArrival = point.type === 'transportation-arrival';
      const shouldHaveLabel = !isTransportationArrival;
      
      // Set marker color based on type
      let markerColor = "#0D9488"; // Default teal
      let useStroke = false;
      let strokeColor = "";
      
      if (point.type === 'tour') {
        markerColor = "#EAB308"; // Yellow for tours
      } else if (point.type === 'restaurant') {
        markerColor = "#F97316"; // Orange for restaurants
      } else if (point.type.startsWith('transportation')) {
        useStroke = true;
        strokeColor = "#EAB308"; // Yellow border
      } else if (point.type === 'accommodation-checkin') {
        markerColor = "#16A34A"; // Green for check-in
      } else if (point.type === 'accommodation-checkout') {
        markerColor = "#DC2626"; // Red for check-out
      } else if (point.type === 'flight-departure') {
        markerColor = "#2563EB"; // Blue for flight departure
        useStroke = true;
        strokeColor = "#0D9488"; // Teal border
      } else if (point.type === 'flight-arrival') {
        markerColor = "#4338CA"; // Indigo for flight arrival
        useStroke = true; 
        strokeColor = "#0D9488"; // Teal border
      } else if (point.type === 'flight') {
        markerColor = "#2563EB"; // Blue for flights
      }
      
      // Get marker number
      const markerNumber = shouldHaveLabel ? visibleMarkerCounter++ : null;
      
      // Create marker
      const marker = new window.google.maps.Marker({
        position,
        map,
        title: point.title,
        label: shouldHaveLabel ? {
          text: `${markerNumber}`,
          color: "#FFFFFF",
          fontWeight: "bold"
        } : null,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeWeight: useStroke ? 2 : 0,
          strokeColor: strokeColor,
          scale: isTransportationArrival ? 8 : 12 // Smaller for arrival points
        },
        zIndex: isTransportationArrival ? index - 1 : index
      });
      
      markersRef.current.push(marker);
      
      // Store point data on marker
      marker.set("pointId", point.id);
      marker.set("markerIndex", markerNumber);
      
      // Create info window
      const infowindow = new window.google.maps.InfoWindow({
        content: `<div class="p-2 text-sm"><strong>${markerNumber ? `${markerNumber}. ` : ''}${point.title}</strong></div>`
      });
      
      // Add click event
      marker.addListener("click", () => {
        if (activeInfoWindow.current) {
          activeInfoWindow.current.close();
        }
        
        infowindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });
        
        activeInfoWindow.current = infowindow;
      });
    });

    // Draw dashed lines between transportation points
      Object.values(transportationPointsByID).forEach(points => {
        if (points.length >= 2) {
          try {
          // Sort to ensure departure comes first
            const sortedTransPoints = [...points].sort((a, b) => {
              if (a.type === 'transportation-departure') return -1;
              if (b.type === 'transportation-departure') return 1;
              return 0;
            });
            
            if (sortedTransPoints[0].coordinates && sortedTransPoints[1].coordinates) {
              const dashedLine = new window.google.maps.Polyline({
                path: [
                  { 
                    lat: Number(sortedTransPoints[0].coordinates.lat), 
                    lng: Number(sortedTransPoints[0].coordinates.lng) 
                  },
                  { 
                    lat: Number(sortedTransPoints[1].coordinates.lat), 
                    lng: Number(sortedTransPoints[1].coordinates.lng) 
                  }
                ],
                geodesic: true,
                strokeColor: '#0D9488',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                strokePattern: [8, 4]
              } as any);
              dashedLine.setMap(map);
            }
          } catch (e) {
            console.error('Error drawing transportation line:', e);
          }
        }
      });
      
    // Create line connecting other points
      try {
      const nonTransportationPoints = pointsRef.current.filter(point => 
          !point.type.startsWith('transportation') && 
          !point.type.startsWith('accommodation-')
        );
        
        if (nonTransportationPoints.length > 1) {
          const validPathPoints: google.maps.LatLngLiteral[] = [];
          
          nonTransportationPoints.forEach(point => {
            if (point.coordinates && 
                typeof point.coordinates.lat !== 'undefined' && 
                typeof point.coordinates.lng !== 'undefined') {
              validPathPoints.push({
                lat: Number(point.coordinates.lat),
                lng: Number(point.coordinates.lng)
              });
            }
          });
          
          if (validPathPoints.length > 1) {
            const polyline = new window.google.maps.Polyline({
              path: validPathPoints,
              geodesic: false,
              strokeColor: '#0D9488',
              strokeOpacity: 0.7,
              strokeWeight: 2
            });
            polyline.setMap(map);
          }
        }
      } catch (e) {
        console.error('Error drawing regular line:', e);
    }

    // Adjust map to fit all markers
    if (pointsRef.current.length > 1) {
      map.fitBounds(bounds);
    } else if (pointsRef.current.length === 1 && pointsRef.current[0].coordinates) {
      const point = pointsRef.current[0];
      map.setCenter({
        lat: Number(point.coordinates.lat),
        lng: Number(point.coordinates.lng)
      });
      map.setZoom(15);
    }
  }, [mapsLoaded, pointCount]); // depend on pointCount to re-render when points change
  
  return (
    <div className="h-full w-full relative">
      <div 
        ref={mapRef} 
        className="h-full w-full" 
        id={`mobile-quotation-map-${versionTag}`}
      ></div>
    </div>
  );
} 